import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, fromEvent, switchMap } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ShortcutService } from './core/services/shortcut-binding.service';
import { LoadingService } from './core/services/loading.service';
import { MainLoadingComponent } from './shared/components/main-loading/main-loading.component';
import { PromptUpdateService } from './core/services/prompt-update.service';
import { NotificationsPanelComponent } from './shared/components/notifications-panel/notifications-panel-component';
import { NotificationsPanelService } from './core/services/notifications-panel.service';
import { AppNotificationsService } from './core/services/app-notifications.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { SessionPollService } from './auth/session-poll.service';
import { SignedInSentinel } from './core/global/constants/constant';
import { Shared } from './shared';

@Component({
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    MainLoadingComponent,
    NotificationsPanelComponent,
    Shared,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private readonly signedInSentinel = SignedInSentinel;
  private destroyRef = inject(DestroyRef);
  private promptUpdateService = inject(PromptUpdateService);
  private router = inject(Router);
  private modalService = inject(NgbModal);
  private sessionPoll = inject(SessionPollService);
  private shortcutService = inject(ShortcutService);

  public loadingService = inject(LoadingService);
  public oidc = inject(OidcSecurityService);
  public notificationsPanelService = inject(NotificationsPanelService);
  public appNotificationsService = inject(AppNotificationsService);

  constructor() {
    fromEvent<KeyboardEvent>(window, 'keydown')
      .pipe(
        switchMap(event => this.oidc.isAuthenticated().pipe(
          take(1),
          map(isAuthenticated => ({event, isAuthenticated}))
        )),
        filter(({ isAuthenticated }) => isAuthenticated),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(({ event }) => this.shortcutService.handleKeydown(event));

    this.closeAllDialogs();
  }

  public async ngOnInit(): Promise<void> {
    const wasSignedIn = sessionStorage.getItem(this.signedInSentinel) === 'true';

    this.oidc.checkAuth().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({isAuthenticated, userData}) => {
      this.getNotifications();

      if (isAuthenticated && userData) {
        sessionStorage.setItem(this.signedInSentinel, 'true');
        this.sessionPoll.start();
        return;
      }

      this.sessionPoll.stop();
      sessionStorage.removeItem(this.signedInSentinel);

      const onLogoutRoute = window.location.pathname.startsWith('/auth/logout');

      if (wasSignedIn && !onLogoutRoute) {
        this.oidc.authorize();
      }
    });
  }

  private closeAllDialogs(): void {
    this.router.events.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (this.modalService.hasOpenModals()) {
          this.modalService.dismissAll();
        }
      }
    });
  }

  private getNotifications(): void {
    this.appNotificationsService.getNotifications();
  }
}
